/**
 * Player options (the original's in-game Options menu), persisted in localStorage and applied
 * live through change events. Other modules read the typed values and subscribe to the section
 * they care about, e.g. the automap:
 *
 * ```ts
 * import { settings } from './settings';
 * const { size, fade, center, showParty, showNames } = settings.value.automap;
 * const off = settings.on('automap', (a) => redraw(a)); // call off() to unsubscribe
 * ```
 *
 * Writers patch one section at a time: `settings.set('automap', { fade: false })`.
 */

export type LightingQuality = 'high' | 'medium' | 'low';
export type NpcSpeech = 'audiotext' | 'audio' | 'text';

export interface Settings {
  sound: {
    /** Effects volume 0..1 (the "Sound" slider). */
    sfx: number;
    /** Music volume 0..1. */
    music: number;
    /** Stereo spread of positional sounds 0..1 (the "3D Bias" slider). */
    separation: number;
    /** How NPC dialogue is delivered. */
    speech: NpcSpeech;
  };
  video: {
    /** High: coloured light; medium: monochrome light; low: monochrome, player light only. */
    lighting: LightingQuality;
    blendedShadows: boolean;
    /** Sliders 0..1, 0.5 = neutral. */
    gamma: number;
    contrast: number;
  };
  automap: {
    size: 'full' | 'mini';
    right: boolean;
    fade: boolean;
    /** Re-center the automap when it is cleared/reopened. */
    center: boolean;
    showParty: boolean;
    showNames: boolean;
  };
  gameplay: {
    /** Pick up gold walked near without clicking it (a D2R option; the original has none). */
    autoGold: boolean;
  };
}

export type Section = keyof Settings;

export const DEFAULT_SETTINGS: Settings = {
  sound: { sfx: 0.9, music: 0.55, separation: 0.75, speech: 'audiotext' },
  video: { lighting: 'high', blendedShadows: true, gamma: 0.5, contrast: 0.5 },
  automap: { size: 'full', right: true, fade: true, center: true, showParty: true, showNames: true },
  gameplay: { autoGold: true },
};

const STORAGE_KEY = 'd2web.settings';

type KV = Pick<Storage, 'getItem' | 'setItem'>;
type Listener<S extends Section> = (value: Settings[S]) => void;

export class SettingsStore {
  private current: Settings;
  private readonly listeners = new Map<Section, Set<Listener<Section>>>();

  constructor(private readonly storage: KV | null) {
    this.current = SettingsStore.merge(DEFAULT_SETTINGS, this.load());
  }

  get value(): Readonly<Settings> {
    return this.current;
  }

  set<S extends Section>(section: S, patch: Partial<Settings[S]>): void {
    this.current = { ...this.current, [section]: { ...this.current[section], ...patch } };
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.current));
    } catch {
      /* storage full or disabled: keep the in-memory value */
    }
    for (const l of this.listeners.get(section) ?? []) l(this.current[section]);
  }

  /** Subscribes to a section; returns the unsubscribe function. */
  on<S extends Section>(section: S, l: Listener<S>): () => void {
    let set = this.listeners.get(section);
    if (!set) this.listeners.set(section, (set = new Set()));
    set.add(l as Listener<Section>);
    return () => set.delete(l as Listener<Section>);
  }

  private load(): unknown {
    try {
      const s = this.storage?.getItem(STORAGE_KEY);
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  }

  /** Overlays saved values on the defaults, keeping only known keys of the right type. */
  private static merge(base: Settings, saved: unknown): Settings {
    const out = structuredClone(base);
    if (!saved || typeof saved !== 'object') return out;
    for (const section of Object.keys(base) as Section[]) {
      const src = (saved as Record<string, unknown>)[section];
      if (!src || typeof src !== 'object') continue;
      const dst = out[section] as unknown as Record<string, unknown>;
      for (const [k, v] of Object.entries(src)) if (k in dst && typeof v === typeof dst[k]) dst[k] = v;
    }
    return out;
  }
}

export const settings = new SettingsStore(typeof localStorage === 'undefined' ? null : localStorage);
